import {
  buildAnnouncementKey,
  markReminderDismissed,
  REMINDER_LEAD_MINUTES,
} from './index'
import { minutesToMs } from '@thyme/libs/src/time/convert'
import { formatCallTimeRange } from '@/libs/date/plannedCallDateFormatting'
import type { TimedUpcomingCall, UpcomingCall } from './types'
import type { TToastMessage } from '@thyme/nashville/src/components/toast/types'
import type { useTToast } from '@thyme/nashville/src/components/toast/useTToast'

const UNKNOWN_MEMBER_NAME = 'Unknown Member'

/**
 * Scheduled call w/time arm of the union: date-only calls need different copy, no
 * `life`, and no time range (IMPROVEME(MET-3034)), so they get a sibling
 * builder and a dispatcher on `isDateOnly`
 * @param call - A call already confirmed to be inside the reminder window.
 * @returns The message to pass to `useTToast().add()`.
 */
export function toTimedCallToastMessage(
  call: TimedUpcomingCall
): TToastMessage {
  return {
    id: buildAnnouncementKey(call),
    headerTitle: `Starts in ${REMINDER_LEAD_MINUTES} minutes`,
    summary: `Scheduled call with ${call.patientName || UNKNOWN_MEMBER_NAME}`,
    detail: formatCallTimeRange(call.startTime, call.endTime),
    actionLabel: 'Open member profile',
    // Tied to the lead time rather than a fixed duration, so the toast (typically)
    // auto-dismisses as the call begins.
    life: minutesToMs(REMINDER_LEAD_MINUTES),
    actionTo: {
      name: '#patientProfile',
      params: { patientId: call.patientId },
      query: { commId: call.communicationId },
    },
  }
}

/**
 * Takes the toast stack as an argument rather than calling `useTToast()` itself, so the
 * loop can be tested against a stub. Date-only calls are already filtered upstream
 * (IMPROVEME(MET-3034)); the check here narrows the union rather than re-filtering.
 * @param toast - The toast stack, i.e. `useTToast()`.
 * @param calls - The calls handed over by `useUpcomingCallReminders`.
 */
export function announceCallReminders(
  toast: ReturnType<typeof useTToast>,
  calls: UpcomingCall[]
): void {
  for (const call of calls) {
    if (!call.isDateOnly) {
      toast.add(toTimedCallToastMessage(call))
    }
  }
}

/**
 * Remembers user-dismissed toast (not auto-dismissed) so will not re-announce this session
 * Ignores PV-generated toast ids, only tracks our scheduled call string communication:datetime ids
 * @param message - The message the user dismissed.
 */
export function dismissCallReminder(message: TToastMessage): void {
  if (typeof message.id !== 'string') {
    return
  }
  markReminderDismissed(message.id)
}
